import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Popover } from "@base-ui-components/react";
import clsx from "clsx";
import { type ComponentState } from "src/models/types/common.types";
import type { Merge } from "src/models/utility";
import styling from "./Dropdown.root.module.scss";

export namespace DropdownRoot {
	export type ElementName = "div";
	export type ElementType = HTMLDivElement;
	export type Context = {
		state?: ComponentState;
		open: boolean;
		toggle: () => void;
		openMenu: () => void;
		closeMenu: () => void;
		closeOnInteractInside: boolean;
	};
	export type Props = Merge<
		Omit<Popover.Root.Props, "children">,
		{
			state?: ComponentState;
			open?: boolean;
			onOpenChange?: (open: boolean) => void;
			defaultOpen?: boolean;
			closeOnInteractInside?: boolean;
			className?: string;
			children?: React.ReactNode;
		}
	>;
}

const DropdownContext = createContext<DropdownRoot.Context | null>(null);

export const useDropdownContext = () => {
	const context = useContext(DropdownContext);
	if (!context) {
		throw new Error("useDropdownContext must be used within a DropdownRoot");
	}
	return context;
};

export const DropdownRoot = (props: DropdownRoot.Props) => {
	const {
		className,
		state = "default",
		children,
		open: openProp,
		onOpenChange,
		defaultOpen = false,
		closeOnInteractInside = true,
		...rest
	} = props;

	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const isControlled = typeof openProp === "boolean";
	const open = isControlled ? (openProp as boolean) : uncontrolledOpen;

	const setOpen = useCallback(
		(next: boolean) => {
			if (!isControlled) setUncontrolledOpen(next);
			onOpenChange?.(next);
		},
		[isControlled, onOpenChange],
	);

	const closeMenu = useCallback(() => setOpen(false), [setOpen]);
	const openMenu = useCallback(() => setOpen(true), [setOpen]);
	const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

	const context = useMemo(
		() => ({
			state,
			open,
			toggle,
			openMenu,
			closeMenu,
			closeOnInteractInside,
		}),
		[state, open, toggle, closeMenu, openMenu, closeOnInteractInside],
	);

	return (
		<DropdownContext.Provider value={context}>
			<Popover.Root open={open} onOpenChange={setOpen} {...rest}>
				<div className={clsx(styling["root"], className)}>{children}</div>
			</Popover.Root>
		</DropdownContext.Provider>
	);
};
